/**
 * 通道分组 —— 可见性的配置单位。
 *
 * 这一页回答两件事：**有哪些组**，以及**每个组现在管着什么**。
 * 第二件比第一件重要得多：一个组的通道数与成员数决定了它能不能删，
 * 也决定了往它里面加一条通道会让谁立刻看见新通道。
 *
 * 分组本身不是权限维度，它只是给「哪些通道」那条既有判定多了一条供给路径。
 * 成员配到组上，就看得见组里全部通道，包括之后加进这个组的 —— 这正是它
 * 相对逐条勾选的全部价值：新通道归到哪个组，一次就决定了谁能看见它。
 */

import { useState } from 'react';
import { request } from '../lib/api.ts';
import { useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Button, Card, ErrorBox, Field, Modal, TextInput,
} from '../components/ui.tsx';
import { DataTable, type Column } from '../components/table.tsx';
import { formatDateTime } from '../lib/format.ts';
import type { ChannelGroup } from '../lib/types.ts';

export function ChannelGroupsPage() {
  const session = useSession();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ChannelGroup | null>(null);

  const list = useAsync<{ data: readonly ChannelGroup[] }>('/v1/channel-groups', {}, []);
  const canManage = session.isPlatform && session.can('admin');

  const columns: readonly Column<ChannelGroup>[] = [
    { key: 'name', header: '分组', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.name}</span>
        {row.description !== null && row.description !== '' && (
          <span className="cell-muted" style={{ fontSize: 11 }}>{row.description}</span>
        )}
      </div>
    ) },
    /*
     * 「空组」要显式标出来。一个没有通道的组配给成员，那个成员就是
     * 「受限且一条通道都看不到」—— 完全合法，但看起来像配置没生效。
     * 不标的话，运营的下一步多半是把这个组删掉，而那才真的让他变成不受限。
     */
    { key: 'channels', header: '通道数', numeric: true, render: (row) => (
      row.channel_count === 0
        ? <span className="cell-muted">空组</span>
        : <span className="num">{row.channel_count}</span>
    ) },
    { key: 'members', header: '配着的成员', numeric: true, render: (row) => (
      row.member_count === 0
        ? <span className="cell-muted">—</span>
        : <span className="num">{row.member_count}</span>
    ) },
    { key: 'created', header: '创建时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
  ];

  return (
    <>
      <div className="row between">
        <h1>通道分组</h1>
        {canManage && (
          <Button variant="primary" onClick={() => { setCreating(true); }}>新建分组</Button>
        )}
      </div>

      <p className="cell-muted" style={{ marginTop: 0 }}>
        给成员配分组，他就看得见组里全部通道，<strong>包括之后加进这个组的</strong>。
        新建通道时必须选一个组 —— 那一步就决定了这条通道对谁可见。
      </p>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.data?.data ?? []}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          {...(canManage ? { onRowClick: (row: ChannelGroup) => { setEditing(row); } } : {})}
          empty="还没有分组"
        />
      </Card>

      {creating && (
        <GroupModal
          onClose={() => { setCreating(false); }}
          onDone={() => { setCreating(false); list.reload(); }}
        />
      )}
      {editing !== null && (
        <GroupModal
          group={editing}
          onClose={() => { setEditing(null); }}
          onDone={() => { setEditing(null); list.reload(); }}
        />
      )}
    </>
  );
}

function GroupModal({
  group, onClose, onDone,
}: {
  readonly group?: ChannelGroup;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');

  const save = useMutation(async () =>
    group === undefined
      ? await request<ChannelGroup>('/v1/channel-groups', {
          method: 'POST',
          body: { name, description: description.trim() === '' ? null : description },
        })
      : await request<ChannelGroup>(`/v1/channel-groups/${group.id}`, {
          method: 'PATCH',
          body: { name, description: description.trim() === '' ? null : description },
        }),
  );

  const remove = useMutation(async () =>
    await request(`/v1/channel-groups/${group?.id ?? ''}`, { method: 'DELETE' }),
  );

  const blockedReason =
    group === undefined
      ? null
      : group.channel_count > 0
        ? `还有 ${String(group.channel_count)} 条通道在这个组里，先把它们移到别的分组`
        : group.member_count > 0
          ? `还有 ${String(group.member_count)} 个成员配着它。直接删会让他们从「只看这个组」变成不受限`
          : null;

  return (
    <Modal
      title={group === undefined ? '新建分组' : `分组 · ${group.name}`}
      onClose={onClose}
      footer={
        <>
          {group !== undefined && (
            <Button
              variant="danger"
              disabled={remove.pending || blockedReason !== null}
              onClick={() => { void remove.run().then((r) => { if (r !== null) onDone(); }); }}
            >
              {remove.pending ? '删除中…' : '删除'}
            </Button>
          )}
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={save.pending || name.trim() === ''}
            onClick={() => { void save.run().then((r) => { if (r !== null) onDone(); }); }}
          >保存</Button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <ErrorBox error={remove.error} />

      <Field label="组名" hint="会出现在通道列表和成员配置界面上，取一个运营看得懂的名字。">
        <TextInput value={name} onChange={setName} placeholder="例如：现有通道" />
      </Field>

      <Field label="备注" hint="可选。写清楚这个组是干什么的，省得半年后没人记得。">
        <TextInput value={description} onChange={setDescription} placeholder="选填" />
      </Field>

      {/*
        * 删不掉的理由要当场说清楚，而不是等人点了删除再弹一个 409。
        * 第二条尤其要讲明后果：删掉一个还有人配着的空组，那些人会从
        * 「看不到任何通道」跳回「不受限」—— 这是放权，而且没有任何提示。
        */}
      {blockedReason !== null && (
        <div className="alert" data-tone="warn">
          <span className="alert-title">这个分组现在删不掉</span>
          <span>{blockedReason}。</span>
        </div>
      )}
    </Modal>
  );
}
